import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { usePdf, usePdfSignedUrl } from '../../../queries/pdfs';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import LaserPointerOverlay from '../../../components/presentation/LaserPointerOverlay';
import HighlightPointerOverlay from '../../../components/presentation/HighlightPointerOverlay';
import SketchOverlayCanvas from '../../../components/presentation/SketchOverlayCanvas';
import EventOverlay from '../../../components/presentation/EventOverlay';
import PresentationTopBar from '../../../components/presentation/PresentationTopBar';
import FloatingCloseButton from '../../../components/primitives/FloatingCloseButton';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface LaserPointer {
  x: number;
  y: number;
  visible: boolean;
  timestamp: number;
}

interface HighlightPointer {
  x: number;
  y: number;
  radius: number;
  visible: boolean;
  timestamp: number;
}

interface SketchPoint {
  x: number;
  y: number;
}

interface SketchStroke {
  points: SketchPoint[];
  color: string;
  width: number;
}

interface MagnifierPointer {
  x: number;
  y: number;
  scale: number;
  visible: boolean;
  timestamp: number;
}

interface EventOverlay {
  visible: boolean;
  url: string;
}

export default function PresentationViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: initialPdf, isLoading: pdfQueryLoading } = usePdf(id ?? null);
  const { data: signedUrl } = usePdfSignedUrl(id ?? null, initialPdf?.file_path ?? null);
  const [loading, setLoading] = useState(true);
  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isCssFullscreen, setIsCssFullscreen] = useState<boolean>(false);
  const [fullscreenDimensions, setFullscreenDimensions] = useState({ width: 0, height: 0 });
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });

  const [laserPointer, setLaserPointer] = useState<LaserPointer>({ x: 0, y: 0, visible: false, timestamp: 0 });
  const [highlightPointer, setHighlightPointer] = useState<HighlightPointer>({ x: 0, y: 0, radius: 15, visible: false, timestamp: 0 });
  const [magnifierPointer, setMagnifierPointer] = useState<MagnifierPointer>({ x: 0, y: 0, scale: 2, visible: false, timestamp: 0 });
  const [sketchStrokes, setSketchStrokes] = useState<SketchStroke[]>([]);
  const [eventOverlay, setEventOverlay] = useState<EventOverlay>({ visible: false, url: '' });

  const pdfDisplayRef = useRef<HTMLDivElement>(null);
  const sketchCanvasRef = useRef<HTMLCanvasElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pdfPageWrapperRef = useRef<HTMLDivElement>(null);
  const laserChannelRef = useRef<any>(null);
  const laserTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentPageRef = useRef<number>(1);
  const fullscreenRequestTimestamp = useRef<string | null>(null);
  const pdfLoadedRef = useRef<boolean>(false);
  const lastUpdateTime = useRef<number>(0);
  const isLocalUpdate = useRef<boolean>(false);

  // 인증 확인
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) navigate('/login');
    };
    checkAuth();
  }, [navigate]);

  // PDF 데이터 + URL이 준비되면 로컬 state 초기화
  useEffect(() => {
    if (initialPdf && signedUrl && !pdfData) {
      setPdfData(initialPdf);
      setPageNumber(initialPdf.current_page || 1);
      currentPageRef.current = initialPdf.current_page || 1;
      setNumPages(initialPdf.page_count || 0);
      setPdfUrl(signedUrl);
      setLoading(false);
    }
    if (!pdfQueryLoading && !initialPdf && id) {
      navigate('/dashboard');
    }
  }, [initialPdf, signedUrl, pdfData, pdfQueryLoading, id, navigate]);


  // 전체화면 진입
  const enterFullscreen = async (startFromFirst: boolean = true) => {
    if (!pdfData) return;

    if (startFromFirst) {
      setPageNumber(1);
      currentPageRef.current = 1;
      await supabase
        .from('pdf_files')
        .update({ current_page: 1 })
        .eq('id', pdfData.id);
    }

    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        await (elem as any).webkitRequestFullscreen();
      }
    } catch {
      // 실패해도 CSS 전체화면 활성화
    }

    setIsCssFullscreen(true);
    setFullscreenDimensions({ width: window.innerWidth, height: window.innerHeight });
    document.body.style.overflow = 'hidden';
  };

  // 전체화면 종료
  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setIsCssFullscreen(false);
    document.body.style.overflow = '';
  };

  // 전체화면 요청 처리
  const tryEnterFullscreen = () => {
    if (isCssFullscreen) return;
    enterFullscreen(false);

    if (pdfData && fullscreenRequestTimestamp.current) {
      setTimeout(async () => {
        await supabase
          .from('pdf_files')
          .update({ fullscreen_request: null })
          .eq('id', pdfData.id);
        fullscreenRequestTimestamp.current = null;
      }, 1000);
    }
  };

  // 스케치 캔버스 그리기
  const drawSketchOnCanvas = (
    canvas: HTMLCanvasElement | null,
    strokes: SketchStroke[]
  ) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.clearRect(0, 0, rect.width, rect.height);

    strokes.forEach(stroke => {
      if (stroke.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const firstPoint = stroke.points[0];
      ctx.moveTo((firstPoint.x / 100) * rect.width, (firstPoint.y / 100) * rect.height);
      for (let i = 1; i < stroke.points.length; i++) {
        const point = stroke.points[i];
        ctx.lineTo((point.x / 100) * rect.width, (point.y / 100) * rect.height);
      }
      ctx.stroke();
    });
  };

  // 스케치 캔버스 업데이트
  useEffect(() => {
    if (sketchCanvasRef.current) {
      drawSketchOnCanvas(sketchCanvasRef.current, sketchStrokes);
    }
  }, [sketchStrokes, isCssFullscreen]);

  // 리사이즈 / ESC / Fullscreen API 변경 감지
  useEffect(() => {
    const handleResize = () => {
      if (isCssFullscreen) {
        setFullscreenDimensions({ width: window.innerWidth, height: window.innerHeight });
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCssFullscreen) {
        exitFullscreen();
      }
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isCssFullscreen) {
        setIsCssFullscreen(false);
        document.body.style.overflow = '';
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isCssFullscreen]);

  // 화살표 키로 페이지 이동
  useEffect(() => {
    if (!pdfData || numPages === 0) return;

    const handleArrowKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        const newPage = Math.min(currentPageRef.current + 1, numPages);
        if (newPage !== currentPageRef.current) {
          setPageNumber(newPage);
          currentPageRef.current = newPage;
          supabase.from('pdf_files')
            .update({ current_page: newPage, updated_at: new Date().toISOString() })
            .eq('id', pdfData.id);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        const newPage = Math.max(currentPageRef.current - 1, 1);
        if (newPage !== currentPageRef.current) {
          setPageNumber(newPage);
          currentPageRef.current = newPage;
          supabase.from('pdf_files')
            .update({ current_page: newPage, updated_at: new Date().toISOString() })
            .eq('id', pdfData.id);
        }
      }
    };

    window.addEventListener('keydown', handleArrowKey);
    return () => window.removeEventListener('keydown', handleArrowKey);
  }, [pdfData, numPages]);

  // 언마운트 시 정리
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 포인터 채널 구독 (RECEIVE only)
  useEffect(() => {
    if (!pdfData) return;

    if (laserChannelRef.current) {
      supabase.removeChannel(laserChannelRef.current);
      laserChannelRef.current = null;
    }

    const channel = supabase.channel(`pointer:${pdfData.id}`, {
      config: { broadcast: { self: false } },
    });

    channel
      .on('broadcast', { event: 'laser' }, ({ payload }) => {
        const age = Date.now() - (payload.timestamp || 0);
        if (!payload.visible) {
          setLaserPointer(prev => prev.visible ? { ...prev, visible: false } : prev);
        } else if (age < 3000) {
          setLaserPointer(payload);
        }
      })
      .on('broadcast', { event: 'highlight' }, ({ payload }) => {
        const age = Date.now() - (payload.timestamp || 0);
        if (!payload.visible) {
          setHighlightPointer(prev => prev.visible ? { ...prev, visible: false } : prev);
        } else if (age < 3000) {
          setHighlightPointer(payload);
        }
      })
      .on('broadcast', { event: 'magnifier' }, ({ payload }) => {
        const age = Date.now() - (payload.timestamp || 0);
        if (!payload.visible) {
          setMagnifierPointer(prev => prev.visible ? { ...prev, visible: false } : prev);
        } else if (age < 3000) {
          setMagnifierPointer(payload);
        }
      })
      .on('broadcast', { event: 'sketch' }, ({ payload }) => {
        if (payload.clear) {
          setSketchStrokes([]);
        } else if (payload.strokes) {
          setSketchStrokes(payload.strokes);
        }
      })
      .on('broadcast', { event: 'page_change' }, ({ payload }) => {
        if (payload.page && payload.page !== currentPageRef.current) {
          setPageNumber(payload.page);
          currentPageRef.current = payload.page;
        }
      });

    channel.subscribe();
    laserChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      laserChannelRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData?.id]);

  // DB 폴링: 페이지 번호 + 전체화면 요청 + 스케치 + 이벤트
  useEffect(() => {
    if (!pdfData) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data } = await supabase
          .from('pdf_files')
          .select('current_page, fullscreen_request, sketch_data, sketch_timestamp, sketch_clear, event_url, event_visible, event_timestamp, event_active, event_number')
          .eq('id', pdfData.id)
          .maybeSingle();

        if (!data) return;
        const now = Date.now();

        // 페이지 업데이트
        if (data.current_page && data.current_page !== currentPageRef.current) {
          if (!isLocalUpdate.current && now - lastUpdateTime.current > 300) {
            setPageNumber(data.current_page);
            currentPageRef.current = data.current_page;
          }
        }

        // 전체화면 요청 감지
        if (data.fullscreen_request) {
          const requestTime = new Date(data.fullscreen_request).getTime();
          const timeDiff = now - requestTime;
          if (timeDiff < 5000 && !isCssFullscreen) {
            fullscreenRequestTimestamp.current = data.fullscreen_request;
            tryEnterFullscreen();
          }
        }

        // 스케치 데이터 업데이트
        if (data.sketch_clear) {
          setSketchStrokes([]);
        } else if (data.sketch_data && Array.isArray(data.sketch_data)) {
          setSketchStrokes(data.sketch_data);
        }

        // 이벤트 오버레이
        if (data.event_active && data.event_visible && data.event_url) {
          setEventOverlay({ visible: true, url: data.event_url });
        } else {
          setEventOverlay(prev => prev.visible ? { visible: false, url: '' } : prev);
        }
      } catch {
        // 무시
      }
    }, 500);

    return () => clearInterval(pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData?.id, isCssFullscreen]);

  // 이벤트 닫기
  const closeEvent = async () => {
    if (!pdfData) return;
    setEventOverlay({ visible: false, url: '' });
    await supabase
      .from('pdf_files')
      .update({ event_visible: false, event_active: false, event_timestamp: Date.now() })
      .eq('id', pdfData.id);
  };

  // PDF 로드 완료
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    pdfLoadedRef.current = true;

    if (fullscreenRequestTimestamp.current) {
      setTimeout(() => tryEnterFullscreen(), 300);
    }
  };

  // 레이저 포인터 핸들러 (PC 전체화면에서 직접 클릭 시)
  const handleLaserClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isCssFullscreen || !pdfDisplayRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = pdfDisplayRef.current.getBoundingClientRect();
    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setLaserPointer({ x, y, visible: true, timestamp: Date.now() });

    if (laserTimeoutRef.current) clearTimeout(laserTimeoutRef.current);
  };

  const handleLaserEnd = () => {
    if (!isCssFullscreen) return;
    if (laserTimeoutRef.current) clearTimeout(laserTimeoutRef.current);
    setLaserPointer(prev => ({ ...prev, visible: false }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  // CSS 전체화면 모드
  if (isCssFullscreen && pdfUrl) {
    return (
      <div
        ref={pdfContainerRef}
        className="fixed inset-0 z-[9999] bg-white flex items-center justify-center"
        style={{ width: '100vw', height: '100vh' }}
      >
        {/* 이벤트 오버레이 */}
        <EventOverlay
          visible={eventOverlay.visible}
          url={eventOverlay.url}
          onClose={closeEvent}
        />

        {/* PDF 표시 */}
        <div
          ref={pdfDisplayRef}
          className="w-full h-full flex items-center justify-center"
          onMouseDown={handleLaserClick}
          onMouseUp={handleLaserEnd}
          onMouseLeave={handleLaserEnd}
          onTouchStart={handleLaserClick}
          onTouchMove={handleLaserClick}
          onTouchEnd={handleLaserEnd}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={fullscreenDimensions.width}
              height={fullscreenDimensions.height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              canvasBackground="white"
            />
          </Document>

          {/* 스케치 오버레이 */}
          <SketchOverlayCanvas
            ref={sketchCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none z-[10000]"
          />

          {/* 레이저 포인터 */}
          <LaserPointerOverlay
            x={laserPointer.x}
            y={laserPointer.y}
            visible={laserPointer.visible}
            className="fixed pointer-events-none z-[10001]"
          />

          {/* 하이라이트 포인터 */}
          <HighlightPointerOverlay
            x={highlightPointer.x}
            y={highlightPointer.y}
            radius={highlightPointer.radius}
            visible={highlightPointer.visible}
            className="fixed inset-0 pointer-events-none z-[10001]"
          />

          {/* 돋보기 포인터 */}
          {magnifierPointer.visible && (
            <div
              className="fixed pointer-events-none z-[10001]"
              style={{
                left: `${magnifierPointer.x}%`,
                top: `${magnifierPointer.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div
                className="rounded-full border-4 border-blue-400/60 shadow-2xl overflow-hidden bg-white relative"
                style={{ width: '200px', height: '200px' }}
              >
                <div className="absolute inset-0" style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      position: 'absolute',
                      width: `${fullscreenDimensions.width * magnifierPointer.scale}px`,
                      height: `${fullscreenDimensions.height * magnifierPointer.scale}px`,
                      left: `${100 - (magnifierPointer.x / 100 * fullscreenDimensions.width * magnifierPointer.scale)}px`,
                      top: `${100 - (magnifierPointer.y / 100 * fullscreenDimensions.height * magnifierPointer.scale)}px`,
                    }}
                  >
                    <Document file={pdfUrl} loading={null} error={null}>
                      <Page
                        pageNumber={pageNumber}
                        width={fullscreenDimensions.width * magnifierPointer.scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            </div>
          )}

          <FloatingCloseButton onClick={exitFullscreen} ariaLabel="전체화면 종료" />
        </div>
      </div>
    );
  }

  // 일반 뷰 (화면 꽉 채우기 + 상단 바)
  const BAR_HEIGHT = 40;

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      <PresentationTopBar
        fileName={pdfData?.file_name?.replace('.pdf', '') || t('common.presentation')}
        pageNumber={pageNumber}
        numPages={numPages}
        hasPdf={!!pdfUrl}
        dashboardLabel={t('common.dashboard')}
        fullscreenLabel={t('common.fullscreen')}
        onBack={() => navigate('/dashboard')}
        onEnterFullscreen={() => enterFullscreen(false)}
        height={BAR_HEIGHT}
      />

      {/* PDF 영역 */}
      <div ref={pdfDisplayRef} className="flex-1 flex items-center justify-center overflow-hidden relative bg-white">
        {pdfUrl ? (
          <div ref={pdfPageWrapperRef} className="flex items-center justify-center">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-400 border-t-transparent"></div>
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={window.innerWidth}
                height={window.innerHeight - BAR_HEIGHT}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                canvasBackground="white"
                onRenderSuccess={() => {
                  const canvas = pdfPageWrapperRef.current?.querySelector('canvas');
                  if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    setPdfPageSize({ width: rect.width, height: rect.height });
                  }
                }}
              />
            </Document>
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <i className="ri-file-pdf-line text-5xl mb-3 block"></i>
            <p>{t('presentation.loading_pdf')}</p>
          </div>
        )}

        {/* 스케치 오버레이 */}
        <SketchOverlayCanvas ref={sketchCanvasRef} />

        {/* 레이저 포인터 */}
        <LaserPointerOverlay
          x={laserPointer.x}
          y={laserPointer.y}
          visible={laserPointer.visible}
        />

        {/* 하이라이트 포인터 */}
        <HighlightPointerOverlay
          x={highlightPointer.x}
          y={highlightPointer.y}
          radius={highlightPointer.radius}
          visible={highlightPointer.visible}
        />

        {/* 돋보기 포인터 */}
        {magnifierPointer.visible && pdfPageSize.width > 0 && (
          <div
            className="absolute pointer-events-none z-20"
            style={{
              left: `${magnifierPointer.x}%`,
              top: `${magnifierPointer.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div
              className="rounded-full border-4 border-blue-400/60 shadow-2xl overflow-hidden bg-white relative"
              style={{ width: '200px', height: '200px' }}
            >
              <div className="absolute inset-0" style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    position: 'absolute',
                    width: `${pdfPageSize.width * magnifierPointer.scale}px`,
                    height: `${pdfPageSize.height * magnifierPointer.scale}px`,
                    left: `${100 - (magnifierPointer.x / 100 * pdfPageSize.width * magnifierPointer.scale)}px`,
                    top: `${100 - (magnifierPointer.y / 100 * pdfPageSize.height * magnifierPointer.scale)}px`,
                  }}
                >
                  <Document file={pdfUrl} loading={null} error={null}>
                    <Page
                      pageNumber={pageNumber}
                      width={pdfPageSize.width * magnifierPointer.scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              </div>
            </div>
            <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          </div>
        )}
      </div>
    </div>
  );
}
